import {useQueryClient} from 'react-query';

interface QueryData<T> {
  data: T;
}

const useQueryHelpers = () => {
  const queryClient = useQueryClient();

  const transformKey = (key: string) => [key];

  const getQueries = <T>(key: any) =>
    queryClient.getQueriesData<QueryData<T>>(transformKey(key));

  const invalidate = (key: any) =>
    queryClient.invalidateQueries(transformKey(key));

  const setData = <T>(key: any, data: any) => {
    const queriesData = queryClient.getQueriesData<T>(transformKey(key));
    if (queriesData.length === 0) return;
    const [_, queryData] = queriesData[0];

    queryClient.setQueriesData(transformKey(key), {...queryData, data});
  };

  const setQueriesData = <T>(key: any, data: any) => {
    const queriesData = queryClient.getQueriesData<T>(transformKey(key));
    if (queriesData.length === 0) return;

    queriesData.forEach((query) => {
      const [_, queryData] = query;
      queryClient.setQueriesData(transformKey(key), {...queryData, data});
    });
  };

  const getData = <T>(key: any) => {
    const queryData = queryClient.getQueryData<T>(transformKey(key));
    if (!queryData) return;

    return (queryData as any).data as T;
  };

  const create = (key: any, data: any) =>
    queryClient.setQueryData(transformKey(key), (cache: any) => ({
      ...cache,
      data: [...cache.data, data],
    }));

  const update = (key: any, data: any) =>
    queryClient.setQueryData(transformKey(key), (cache: any) =>
      cache
        ? {
            ...cache,
            data: cache.data.map((item: any) =>
              item.id === data.id ? data : item,
            ),
          }
        : cache,
    );

  const map = <T>(key: any, updater: (data: T) => T) => {
    const queries =
      queryClient.getQueriesData<QueryData<T[]>>(transformKey(key)) ?? [];

    queries.forEach((query) => {
      const [queryKey, data] = query;

      if (!queryKey || !data) return;

      setData(queryKey, data.data.map(updater));
    });
  };

  const filter = <T>(key: any, updater: (data: T) => boolean) => {
    const queries =
      queryClient.getQueriesData<QueryData<T[]>>(transformKey(key)) ?? [];

    queries.forEach((query) => {
      const [queryKey, data] = query;

      if (!queryKey || !data) return;

      setData(queryKey, data.data.filter(updater));
    });
  };

  const remove = (key: any, data: any) =>
    queryClient.setQueryData(transformKey(key), (cache: any) => ({
      ...cache,
      data: cache.data.filter((item: any) => {
        return item.id !== data.id;
      }),
    }));

  return {
    invalidate,
    setData,
    getData,
    getQueries,
    create,
    update,
    remove,
    map,
    filter,
    setQueriesData,
  };
};

export default useQueryHelpers;
